import { useState } from 'react';
import { motion } from 'framer-motion';
import toast, { Toaster } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import {Loader2, ShieldCheck, Sparkles, Tag} from 'lucide-react';
import { lumi } from '@/lib/lumi';
import { formatCPF, formatPhone, validateCPF, validateEmail, validatePhone } from '@/lib/validators';

interface FormState {
  name: string;
  email: string;
  cpf: string;
  phone: string;
  voucher: string;
}

const initialForm: FormState = {
  name: '',
  email: '',
  cpf: '',
  phone: '',
  voucher: ''
};

// Deno Function endpoint (proxy seguro para o webhook)
const SUBMIT_FUNCTION_URL = 'https://api.lumi.new/v1/functions/p442696802518519808/submitRegistration';

function Home() {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (key: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    if (key === 'cpf') value = formatCPF(value);
    if (key === 'phone') value = formatPhone(value);
    if (key === 'voucher') value = value.toUpperCase().replace(/-/g, '');
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    if (!form.name.trim() || form.name.trim().length < 3) {
      toast.error('Informe seu nome completo.');
      return;
    }
    if (!validateEmail(form.email)) {
      toast.error('E-mail inválido.');
      return;
    }
    if (!validateCPF(form.cpf)) {
      toast.error('CPF inválido.');
      return;
    }
    if (!validatePhone(form.phone)) {
      toast.error('Telefone inválido.');
      return;
    }
    if (!form.voucher.trim()) {
      toast.error('Informe o voucher de desconto.');
      return;
    }

    setSubmitting(true);
    const loading = toast.loading('Validando voucher...');

    try {
      // 1. Validate voucher exists and is available
      const code = form.voucher.trim().toUpperCase();
      const { list } = await lumi.entities.vouchers.list({
        filter: { code },
        limit: 1
      });

      const voucher = list?.[0];
      if (!voucher) {
        toast.dismiss(loading);
        toast.error('Voucher não encontrado.');
        setSubmitting(false);
        return;
      }
      if (voucher.status === 'used') {
        toast.dismiss(loading);
        toast.error('Este voucher já foi utilizado.');
        setSubmitting(false);
        return;
      }
      if (voucher.status === 'disabled') {
        toast.dismiss(loading);
        toast.error('Este voucher está desativado.');
        setSubmitting(false);
        return;
      }

      toast.loading('Finalizando cadastro...', { id: loading });

      const now = new Date().toISOString();

      // 2. Persist registration first (to get the ID for webhook update)
      const registration = await lumi.entities.registrations.create({
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        cpf: form.cpf,
        phone: form.phone,
        voucherCode: code,
        webhookSent: false,
        webhookResponse: 'pending',
        creator: 'public',
        createdAt: now,
        updatedAt: now
      });

      // 3. Mark voucher as used
      await lumi.entities.vouchers.update(voucher._id, {
        status: 'used',
        usedAt: now,
        usedBy: form.email.trim().toLowerCase(),
        updatedAt: now
      });

      // 4. Send to webhook via Deno Function (proxy seguro com retry)
      let loginUrl: string | null = null;
      try {
        const result: any = await lumi.functions.invoke('submitRegistration', {
          method: 'POST',
          body: {
            name: form.name.trim(),
            email: form.email.trim().toLowerCase(),
            cpf: form.cpf,
            phone: form.phone,
            voucher: code,
            discountPercent: voucher.discountPercent ?? 0,
            registrationId: registration?._id ?? ''
          }
        });
        // SDK pode retornar diretamente o body ou envelopado em { data }
        const payload = result?.data ?? result;
        if (payload && typeof payload.loginUrl === 'string' && payload.loginUrl.startsWith('http')) {
          loginUrl = payload.loginUrl;
        }
      } catch (webhookErr: any) {
        // Webhook failure não bloqueia o cadastro — apenas loga
        console.warn('Webhook dispatch failed (non-blocking):', webhookErr?.data?.error ?? webhookErr?.message);
      }

      toast.dismiss(loading);
      toast.success('Cadastro realizado com sucesso!');
      navigate('/success', {
        state: {
          name: form.name.trim(),
          discount: voucher.discountPercent ?? 0,
          code,
          loginUrl
        }
      });
    } catch (error: any) {
      console.error('Registration failed:', error);
      toast.dismiss(loading);
      toast.error('Erro ao processar cadastro. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 relative overflow-hidden">
      <Toaster
        position="top-center"
        toastOptions={{
          style: { background: '#ffffff', color: '#0F172A', border: '1px solid #E2E8F0', boxShadow: '0 10px 30px rgba(15,23,42,0.08)' }
        }} />


      {/* Background light orbs (sky + teal) */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-32 w-[28rem] h-[28rem] bg-sky-300/40 rounded-full blur-3xl" />
        <div className="absolute top-1/3 -left-40 w-[26rem] h-[26rem] bg-teal-300/40 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-[20rem] h-[20rem] bg-sky-200/50 rounded-full blur-3xl" />
      </div>

      <main className="relative z-10 max-w-md mx-auto px-5 pt-8 pb-16">
        {/* Header */}
        <header className="relative flex items-center justify-center mb-8">
          <div className="flex items-center gap-4">
            <img
              src="https://static.lumi.new/material/16/16579d03c8ed6c5cc8f848176b8adb09.png"
              alt="EW Pass"
              className="h-14 w-auto object-contain" />

            <span className="h-10 w-px bg-slate-300" aria-hidden="true" />
            <img
              src="https://static.lumi.new/material/80/803fa85caa94fa9d12d1dbba94fbcdf0.png"
              alt="Logo parceiro"
              className="h-14 w-auto object-contain" />

          </div>
        </header>

        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-6">

          <div className="flex justify-center mb-4">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-sky-100 border border-sky-200 rounded-full text-sky-700 text-xs font-medium">
              <Tag className="w-3 h-3" />
              Parceria Exclusiva EW Pass e Minu
            </div>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold leading-tight tracking-tight mb-2 text-slate-900 text-center">
            Resgate sua <span className="bg-gradient-to-r from-sky-500 to-teal-500 bg-clip-text text-transparent">recompensa</span> EW Pass
          </h1>
          <p className="text-slate-600 text-sm text-center">Preencha seus dados e use o código exclusivo que você recebeu para garantir sua recompensa.

          </p>
        </motion.div>

        {/* Form card */}
        <motion.form
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="bg-white/80 backdrop-blur-xl border border-slate-200 rounded-2xl p-5 space-y-4 shadow-xl shadow-sky-500/5">

          <Field label="Nome completo" required>
            <input
              type="text"
              autoComplete="name"
              value={form.name}
              onChange={handleChange('name')}
              placeholder="Como devemos te chamar?"
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/15 focus:bg-white transition" />

          </Field>

          <Field label="E-mail" required>
            <input
              type="email"
              autoComplete="email"
              inputMode="email"
              value={form.email}
              onChange={handleChange('email')}
              placeholder="seu@email.com"
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/15 focus:bg-white transition" />

          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="CPF" required>
              <input
                type="text"
                inputMode="numeric"
                value={form.cpf}
                onChange={handleChange('cpf')}
                placeholder="000.000.000-00"
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-3 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/15 focus:bg-white transition text-sm" />

            </Field>

            <Field label="Telefone" required>
              <input
                type="tel"
                autoComplete="tel"
                inputMode="tel"
                value={form.phone}
                onChange={handleChange('phone')}
                placeholder="(11) 91234-5678"
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-3 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/15 focus:bg-white transition text-sm" />

            </Field>
          </div>

          <Field label="Voucher de desconto" required>
            <div className="relative">
              <input
                type="text"
                value={form.voucher}
                onChange={handleChange('voucher')}
                placeholder="EWXXXXXXXX"
                className="w-full bg-gradient-to-r from-sky-50 to-teal-50 border border-sky-200 rounded-lg px-4 py-3 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/15 transition font-mono tracking-wider uppercase" />

              <Tag className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-sky-500" />
            </div>
          </Field>

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 disabled:opacity-60 text-white font-bold py-3.5 rounded-lg transition-all flex items-center justify-center gap-2 shadow-lg shadow-sky-500/25">

            {submitting ?
            <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Processando...
              </> :

            <>
                Ativar sua recompensa
                <Sparkles className="w-4 h-4" />
              </>
            }
          </button>

          <p className="flex items-center justify-center gap-1.5 text-xs text-slate-500 pt-1">
            <ShieldCheck className="w-3 h-3" />
            Seus dados são protegidos e usados apenas para validação.
          </p>
        </motion.form>

        {/* Trust badges */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="grid grid-cols-3 gap-3 mt-8">

          {[
          { label: 'instatâneo', value: 'Acesso' },
          { label: 'acesso', value: '30 dias' },
          { label: '+Lucy I.A', value: 'Inglês' }].
          map((item) =>
          <div key={item.label} className="bg-white/70 backdrop-blur-sm border border-slate-200 rounded-xl p-3 text-center shadow-sm">
              <p className="bg-gradient-to-r from-sky-600 to-teal-600 bg-clip-text text-transparent font-bold text-sm">{item.value}</p>
              <p className="text-slate-500 text-xs mt-0.5">{item.label}</p>
            </div>
          )}
        </motion.div>

        <footer className="text-center text-slate-400 text-xs mt-12">
          © {new Date().getFullYear()} EW Pass. Todos os direitos reservados.
        </footer>
      </main>
    </div>);

}

function Field({ label, required, children }: {label: string;required?: boolean;children: React.ReactNode;}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-700 mb-1.5 block">
        {label}
        {required ? <span className="text-sky-500 ml-0.5">*</span> : null}
      </span>
      {children}
    </label>);

}

export default Home;